import React, { useState, useContext, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { UserContext } from '../context/UserContext';
import { ROLE_GROUPS, hasRole } from '../config/navigation';
import { t, getStrings } from '../config/onboardingStrings';
import { STEP_MEDIA, getEstimatedTime, formatTime, HELP_ARTICLES, USER_GOALS, HOW_TO_STEPS, getHowToSteps } from '../config/onboardingMedia';
import { useOnboardingAnalytics } from '../hooks/useOnboardingAnalytics';
import { useAudioNarration, AudioControls } from '../hooks/useAudioNarration';
import SpotlightOverlay from './SpotlightOverlay';
import { HelpCenterLink } from './HelpCenterLink';
import {
  Rocket,
  LayoutDashboard,
  Search,
  Zap,
  Settings,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  ChevronUp,
  X,
  Check,
  Sparkles,
  AlertCircle,
  ArrowLeftRight,
  GitBranch,
  FlaskConical,
  FileSignature,
  Users,
  UserCog,
  DollarSign,
  PieChart,
  Play,
  Keyboard,
  Clock,
  BookOpen,
  Building,
  Building2,
  GraduationCap,
  BarChart3,
  FolderOpen,
  FileText,
  TrendingUp,
  AlertTriangle,
  Workflow,
  ListChecks,
  HelpCircle,
  UserCircle,
  Layers,
  Target,
  Briefcase,
  ClipboardCheck,
  Calculator,
  Lightbulb,
  Info,
  ExternalLink,
  FormInput,
  FileOutput,
  Globe,
  Handshake,
  Receipt,
  TrendingDown,
  UserPlus,
} from 'lucide-react';
import api, { setTourSandboxMode } from '../utils/api';

// CSS keyframes for animations
const animationStyles = `
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes pulse-glow {
    0%, 100% {
      box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.4);
    }
    50% {
      box-shadow: 0 0 0 8px rgba(59, 130, 246, 0);
    }
  }

  @keyframes bounce-subtle {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-4px); }
  }

  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }

  .animate-fadeInUp {
    animation: fadeInUp 0.4s ease-out forwards;
  }

  .animate-fadeIn {
    animation: fadeIn 0.3s ease-out forwards;
  }

  .animate-pulse-glow {
    animation: pulse-glow 2s ease-in-out infinite;
  }

  .animate-bounce-subtle {
    animation: bounce-subtle 2s ease-in-out infinite;
  }

  .animate-shimmer {
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
    background-size: 200% 100%;
    animation: shimmer 2s infinite;
  }

  .video-container {
    position: relative;
    border-radius: 0.75rem;
    overflow: hidden;
    background: #000;
  }

  .video-container video,
  .video-container img {
    width: 100%;
    height: auto;
    display: block;
  }

  .progress-bar-container {
    position: relative;
    height: 6px;
    background: rgba(148, 163, 184, 0.3);
    border-radius: 9999px;
    overflow: hidden;
  }

  .progress-bar-fill {
    height: 100%;
    border-radius: 9999px;
    transition: width 0.3s ease-out;
  }

  .progress-bar-segments {
    position: absolute;
    inset: 0;
    display: flex;
    gap: 2px;
  }

  .progress-segment {
    flex: 1;
    height: 100%;
    border-radius: 2px;
  }
`;

/**
 * StepMedia - Renders video, GIF, or image for a step
 * Media assets are optional - component gracefully handles missing files
 */
const StepMedia = ({ stepId, locale, onPlay, onEnd }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const [mediaError, setMediaError] = useState(false);
  const media = STEP_MEDIA[stepId];

  // Don't render if no media config or if media failed to load
  if (!media || mediaError) return null;

  const handlePlayVideo = () => {
    setShowVideo(true);
    setIsPlaying(true);
    onPlay?.();
  };

  const handleVideoEnd = () => {
    setIsPlaying(false);
    onEnd?.();
  };

  const handleMediaError = () => {
    // Hide the entire media component if assets don't exist
    setMediaError(true);
  };

  // Prefer video, then GIF, then thumbnail
  if (media.video && showVideo) {
    return (
      <div className="video-container mb-4">
        <video
          ref={videoRef}
          src={media.video}
          autoPlay
          onEnded={handleVideoEnd}
          onError={() => setShowVideo(false)}
          className="rounded-xl"
        />
        {isPlaying && (
          <button
            onClick={() => {
              videoRef.current?.pause();
              setIsPlaying(false);
            }}
            className="absolute bottom-2 right-2 p-2 bg-black/50 rounded-full text-white hover:bg-black/70"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    );
  }

  if (media.gif || media.thumbnail) {
    return (
      <div className="video-container mb-4 relative group">
        <img
          src={media.gif || media.thumbnail}
          alt={`Step: ${stepId}`}
          className="rounded-xl"
          onError={handleMediaError}
        />
        {media.video && !showVideo && (
          <button
            onClick={handlePlayVideo}
            className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <div className="p-4 bg-blue-600 rounded-full text-white">
              <Play className="w-8 h-8" />
            </div>
          </button>
        )}
      </div>
    );
  }

  return null;
};

/**
 * ProgressBar - Enhanced progress bar with time estimate
 */
const ProgressBar = ({ steps, currentStep, locale }) => {
  const stepIds = steps.map(s => s.id);
  const totalTime = getEstimatedTime(stepIds);
  const completedStepIds = stepIds.slice(0, currentStep);
  const elapsedTime = getEstimatedTime(completedStepIds);
  const remainingTime = totalTime - elapsedTime;
  const progressPercent = ((currentStep + 1) / steps.length) * 100;

  return (
    <div className="mb-4">
      {/* Time estimate */}
      <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400 mb-2">
        <span className="flex items-center gap-1">
          <Clock className="w-3 h-3" />
          ~{formatTime(remainingTime)} remaining
        </span>
        <span>{currentStep + 1} of {steps.length}</span>
      </div>

      {/* Segmented progress bar */}
      <div className="progress-bar-container">
        <div
          className="progress-bar-fill bg-gradient-to-r from-blue-500 to-purple-500"
          style={{ width: `${progressPercent}%` }}
        />
        <div className="progress-bar-segments">
          {steps.map((step, index) => (
            <div
              key={step.id}
              className={`progress-segment ${
                index < currentStep
                  ? 'bg-blue-500/80'
                  : index === currentStep
                  ? 'bg-blue-400/50'
                  : 'bg-transparent'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

/**
 * HowToPanel - Detailed step-by-step instructions for in-depth walkthrough mode
 * Shows numbered instructions, tips, warnings, and try-it actions
 */
const HowToPanel = ({ stepId, onTryIt, isExpanded, onToggle }) => {
  const howTo = getHowToSteps(stepId);

  if (!howTo) return null;

  const { instructions, tips, warnings, prerequisites, tryItAction } = howTo;

  return (
    <div className="mt-4 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
      {/* Header - always visible */}
      <button
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center justify-between bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 hover:from-amber-100 hover:to-orange-100 dark:hover:from-amber-900/30 dark:hover:to-orange-900/30 transition-colors"
      >
        <div className="flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-amber-600 dark:text-amber-400" />
          <span className="text-sm font-medium text-amber-800 dark:text-amber-200">
            Step-by-Step Guide
          </span>
          {instructions && (
            <span className="text-xs text-amber-600 dark:text-amber-400">
              ({instructions.length} steps)
            </span>
          )}
        </div>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4 text-amber-600 dark:text-amber-400" />
        ) : (
          <ChevronDown className="w-4 h-4 text-amber-600 dark:text-amber-400" />
        )}
      </button>

      {/* Expandable content */}
      {isExpanded && (
        <div className="p-4 space-y-4 bg-white dark:bg-slate-900 animate-fadeIn">
          {/* Prerequisites */}
          {prerequisites && prerequisites.length > 0 && (
            <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800">
              <div className="flex items-center gap-2 mb-2">
                <Info className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                <span className="text-xs font-semibold text-blue-700 dark:text-blue-300 uppercase tracking-wide">
                  Before you start
                </span>
              </div>
              <ul className="space-y-1">
                {prerequisites.map((prereq, idx) => (
                  <li key={idx} className="text-sm text-blue-700 dark:text-blue-300 flex items-start gap-2">
                    <span className="text-blue-400">•</span>
                    {prereq}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Instructions */}
          {instructions && instructions.length > 0 && (
            <div className="space-y-2">
              {instructions.map((instruction, idx) => (
                <div
                  key={idx}
                  className="flex items-start gap-3 p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                >
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center">
                    <span className="text-xs font-bold text-blue-600 dark:text-blue-400">
                      {instruction.step}
                    </span>
                  </div>
                  <p className="text-sm text-slate-700 dark:text-slate-300 pt-0.5">
                    {instruction.text}
                  </p>
                </div>
              ))}
            </div>
          )}

          {/* Tips */}
          {tips && tips.length > 0 && (
            <div className="p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-800">
              <div className="flex items-center gap-2 mb-2">
                <Lightbulb className="w-4 h-4 text-green-600 dark:text-green-400" />
                <span className="text-xs font-semibold text-green-700 dark:text-green-300 uppercase tracking-wide">
                  Pro Tips
                </span>
              </div>
              <ul className="space-y-1">
                {tips.map((tip, idx) => (
                  <li key={idx} className="text-sm text-green-700 dark:text-green-300 flex items-start gap-2">
                    <span className="text-green-400">✓</span>
                    {tip}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Warnings */}
          {warnings && warnings.length > 0 && (
            <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                <span className="text-xs font-semibold text-amber-700 dark:text-amber-300 uppercase tracking-wide">
                  Important
                </span>
              </div>
              <ul className="space-y-1">
                {warnings.map((warning, idx) => (
                  <li key={idx} className="text-sm text-amber-700 dark:text-amber-300 flex items-start gap-2">
                    <span className="text-amber-400">!</span>
                    {warning}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Try It Action */}
          {tryItAction && (
            <div className="pt-2 border-t border-slate-100 dark:border-slate-800">
              <button
                onClick={() => onTryIt?.(tryItAction)}
                className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 rounded-lg transition-all shadow-sm hover:shadow-md"
              >
                <div className="flex items-center gap-2">
                  <Play className="w-4 h-4" />
                  <span>{tryItAction.label}</span>
                </div>
                <div className="flex items-center gap-1 text-white/80 text-xs">
                  <span>{tryItAction.description}</span>
                  <ExternalLink className="w-3 h-3" />
                </div>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * OnboardingWizard - Enhanced step-through introduction for new users
 *
 * Features:
 * - Progress persistence (localStorage + backend)
 * - Role-specific step sets
 * - Interactive feature highlights
 * - Video/GIF tutorials
 * - Progress bar with time estimates
 * - Audio narration support
 * - Help center integration
 * - CSS animations
 * - Keyboard navigation
 * - Analytics tracking
 * - i18n support
 */
const OnboardingWizard = ({
  onComplete,
  onSkip,
  initialStep = 0,
  locale = 'en',
  selectedGoal,
  tourDepth = 'lite', // 'lite' for quick overview, 'detailed' for step-by-step
}) => {
  const { user, refreshUser } = useContext(UserContext);
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(initialStep);
  const [completing, setCompleting] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [showSpotlight, setShowSpotlight] = useState(false);
  const [animationKey, setAnimationKey] = useState(0);
  const [showMedia, setShowMedia] = useState(true);
  const [isMinimized, setIsMinimized] = useState(false);
  const [howToExpanded, setHowToExpanded] = useState(tourDepth === 'detailed'); // Auto-expand in detailed mode

  // Audio narration
  const audioNarration = useAudioNarration();

  const userRoles = (user?.role_slugs || []).map(s => String(s).toLowerCase());
  const isAdmin = hasRole(userRoles, ROLE_GROUPS.ADMIN);
  const isFinance = hasRole(userRoles, ROLE_GROUPS.FINANCE);
  const isHR = hasRole(userRoles, ROLE_GROUPS.HR);
  const isBudgetManager = hasRole(userRoles, ROLE_GROUPS.BUDGET);

  const isMac = typeof navigator !== 'undefined' && navigator.platform.includes('Mac');
  const searchShortcut = isMac ? 'Cmd+K' : 'Ctrl+K';

  // Analytics
  const analytics = useOnboardingAnalytics(user?.id);

  const handleTrySearch = useCallback(() => {
    const event = new KeyboardEvent('keydown', {
      key: 'k',
      metaKey: isMac,
      ctrlKey: !isMac,
      bubbles: true,
    });
    document.dispatchEvent(event);
    analytics.trackInteractiveFeature('globalSearch', 'triggered');
    setShowSpotlight(false);
    setIsMinimized(true);
  }, [analytics, isMac]);

  const handleResume = useCallback(() => {
    setIsMinimized(false);
  }, []);

  // Save progress to localStorage
  const saveProgressToLocal = useCallback((step) => {
    try {
      localStorage.setItem('onboarding_progress', JSON.stringify({
        step,
        timestamp: Date.now(),
        userId: user?.id,
      }));
    } catch (e) {
      console.error('Failed to save onboarding progress to localStorage:', e);
    }
  }, [user?.id]);

  // Load progress from localStorage
  const loadProgressFromLocal = useCallback(() => {
    try {
      const saved = localStorage.getItem('onboarding_progress');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Only restore if same user and less than 24 hours old
        if (
          parsed.userId === user?.id &&
          Date.now() - parsed.timestamp < 24 * 60 * 60 * 1000
        ) {
          return parsed.step;
        }
      }
    } catch (e) {
      console.error('Failed to load onboarding progress:', e);
    }
    return null;
  }, [user?.id]);

  // Clear local progress
  const clearLocalProgress = useCallback(() => {
    try {
      localStorage.removeItem('onboarding_progress');
    } catch (e) {
      // Ignore
    }
  }, []);

  // Restore progress on mount
  useEffect(() => {
    const savedStep = loadProgressFromLocal();
    if (savedStep !== null && savedStep > 0) {
      setCurrentStep(savedStep);
    }
  }, [loadProgressFromLocal]);

  // Define onboarding steps based on user role
  const getSteps = useCallback(() => {
    const steps = [
      // Welcome step
      {
        id: 'welcome',
        title: t(locale, 'welcome.title'),
        description: t(locale, 'welcome.description', {
          name: user?.name?.split(' ')[0] || 'there'
        }),
        icon: Rocket,
        content: (
          <div className="text-center py-6 animate-fadeInUp">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center animate-pulse-glow">
              <Sparkles className="w-10 h-10 text-white animate-bounce-subtle" />
            </div>
            <p className="text-slate-600 dark:text-slate-400 max-w-md mx-auto">
              {t(locale, 'welcome.content')}
            </p>
          </div>
        ),
      },
      // Dashboard step
      {
        id: 'dashboard',
        title: t(locale, 'dashboard.title'),
        description: t(locale, 'dashboard.description'),
        icon: LayoutDashboard,
        action: { label: t(locale, 'dashboard.action'), path: '/dashboard' },
        spotlight: '[data-tour="dashboard-link"]',
        content: (
          <div className="space-y-4 animate-fadeInUp">
            <div className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
              <Check className="w-5 h-5 text-green-500 mt-0.5" />
              <div>
                <div className="font-medium text-slate-800 dark:text-slate-200">
                  {t(locale, 'dashboard.features.tasks.title')}
                </div>
                <div className="text-sm text-slate-500 dark:text-slate-400">
                  {t(locale, 'dashboard.features.tasks.description')}
                </div>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
              <Check className="w-5 h-5 text-green-500 mt-0.5" />
              <div>
                <div className="font-medium text-slate-800 dark:text-slate-200">
                  {t(locale, 'dashboard.features.metrics.title')}
                </div>
                <div className="text-sm text-slate-500 dark:text-slate-400">
                  {t(locale, 'dashboard.features.metrics.description')}
                </div>
              </div>
            </div>
          </div>
        ),
      },
      // Global Search step
      {
        id: 'search',
        title: t(locale, 'search.title'),
        description: t(locale, 'search.description', { shortcut: searchShortcut }),
        icon: Search,
        interactive: true,
        interactiveAction: 'triggerSearch',
        spotlight: '[data-tour="search-button"]',
        content: (
          <div className="text-center py-4 animate-fadeInUp">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
              <span className="text-lg">{isMac ? '\u2318' : 'Ctrl'}</span>
              <span className="text-lg font-mono">K</span>
            </div>
            <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">
              {t(locale, 'search.content')}
            </p>
            <button
              onClick={handleTrySearch}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
            >
              <Play className="w-4 h-4" />
              {t(locale, 'common.tryIt')}
            </button>
          </div>
        ),
      },
      // Quick Actions step
      {
        id: 'quick-actions',
        title: t(locale, 'quickActions.title'),
        description: t(locale, 'quickActions.description'),
        icon: Zap,
        spotlight: '[data-tour="quick-actions"]',
        content: (
          <div className="text-center py-4 animate-fadeInUp">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-blue-600 text-white shadow-lg animate-pulse-glow">
              <Zap className="w-7 h-7" />
            </div>
            <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">
              {t(locale, 'quickActions.content')}
            </p>
          </div>
        ),
      },
    ];

    // Add role-specific steps
    if (isBudgetManager) {
      steps.push({
        id: 'budget-transfers',
        title: t(locale, 'budgetManager.transfers.title'),
        description: t(locale, 'budgetManager.transfers.description'),
        icon: ArrowLeftRight,
        action: { label: t(locale, 'budgetManager.transfers.action'), path: '/transfers' },
        content: (
          <div className="text-center py-4 animate-fadeInUp">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-lg">
              <ArrowLeftRight className="w-7 h-7" />
            </div>
            <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">
              Move funds between cost centers with full audit trails.
            </p>
          </div>
        ),
      });

      steps.push({
        id: 'budget-scenarios',
        title: t(locale, 'budgetManager.scenarios.title'),
        description: t(locale, 'budgetManager.scenarios.description'),
        icon: GitBranch,
        action: { label: t(locale, 'budgetManager.scenarios.action'), path: '/budget/scenarios' },
        content: (
          <div className="text-center py-4 animate-fadeInUp">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 text-white shadow-lg">
              <GitBranch className="w-7 h-7" />
            </div>
            <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">
              Model different budget allocations and compare outcomes.
            </p>
          </div>
        ),
      });
    }

    // Researcher-specific steps
    if (hasRole(userRoles, ['researcher', 'pi', 'research_admin'])) {
      steps.push({
        id: 'research-portfolio',
        title: t(locale, 'researcher.portfolio.title'),
        description: t(locale, 'researcher.portfolio.description'),
        icon: FlaskConical,
        action: { label: t(locale, 'researcher.portfolio.action'), path: '/research' },
        content: (
          <div className="text-center py-4 animate-fadeInUp">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 text-white shadow-lg">
              <FlaskConical className="w-7 h-7" />
            </div>
            <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">
              Track grants, sponsored projects, and funding sources.
            </p>
          </div>
        ),
      });

      steps.push({
        id: 'research-commitments',
        title: t(locale, 'researcher.commitments.title'),
        description: t(locale, 'researcher.commitments.description'),
        icon: FileSignature,
        action: { label: t(locale, 'researcher.commitments.action'), path: '/research?tab=commitments' },
        content: (
          <div className="text-center py-4 animate-fadeInUp">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 text-white shadow-lg">
              <FileSignature className="w-7 h-7" />
            </div>
            <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">
              Manage cost-sharing and commitment obligations.
            </p>
          </div>
        ),
      });
    }

    // HR-specific steps
    if (isHR) {
      steps.push({
        id: 'positions',
        title: t(locale, 'hr.positions.title'),
        description: t(locale, 'hr.positions.description'),
        icon: Users,
        action: { label: t(locale, 'hr.positions.action'), path: '/transfers' },
        content: (
          <div className="text-center py-4 animate-fadeInUp">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-indigo-500 to-blue-600 text-white shadow-lg">
              <Users className="w-7 h-7" />
            </div>
            <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">
              Shift budget between cost centers as staffing needs change.
            </p>
          </div>
        ),
      });

      steps.push({
        id: 'personnel-whatif',
        title: t(locale, 'hr.whatIf.title'),
        description: t(locale, 'hr.whatIf.description'),
        icon: UserCog,
        action: { label: t(locale, 'hr.whatIf.action'), path: '/positions/budgeting' },
        content: (
          <div className="text-center py-4 animate-fadeInUp">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-rose-500 to-pink-600 text-white shadow-lg">
              <UserCog className="w-7 h-7" />
            </div>
            <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">
              Model staffing changes and see financial impacts instantly.
            </p>
          </div>
        ),
      });
    }

    // Finance-specific steps
    if (isFinance) {
      steps.push({
        id: 'revenue-recognition',
        title: t(locale, 'finance.revenue.title'),
        description: t(locale, 'finance.revenue.description'),
        icon: DollarSign,
        action: { label: t(locale, 'finance.revenue.action'), path: '/budget/revenue-recognition' },
        content: (
          <div className="text-center py-4 animate-fadeInUp">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 text-white shadow-lg">
              <DollarSign className="w-7 h-7" />
            </div>
            <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">
              Manage deferred revenue and recognition schedules.
            </p>
          </div>
        ),
      });

      steps.push({
        id: 'program-margins',
        title: t(locale, 'finance.margins.title'),
        description: t(locale, 'finance.margins.description'),
        icon: PieChart,
        action: { label: t(locale, 'finance.margins.action'), path: '/budget/margins' },
        content: (
          <div className="text-center py-4 animate-fadeInUp">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 text-white shadow-lg">
              <PieChart className="w-7 h-7" />
            </div>
            <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">
              Analyze program profitability and identify opportunities.
            </p>
          </div>
        ),
      });
    }

    // Campus & Department Management steps (available to most users)
    steps.push({
      id: 'campus-summary',
      title: t(locale, 'campusSummary.title'),
      description: t(locale, 'campusSummary.description'),
      icon: Building2,
      action: { label: t(locale, 'campusSummary.action'), path: '/campus' },
      content: (
        <div className="space-y-3 animate-fadeInUp">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-slate-500 to-slate-700 text-white shadow-lg mx-auto block">
            <Building2 className="w-7 h-7" />
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
              <Check className="w-4 h-4 text-green-500" />
              Institution-wide financial metrics
            </div>
            <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
              <Check className="w-4 h-4 text-green-500" />
              Cross-department comparisons
            </div>
            <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
              <Check className="w-4 h-4 text-green-500" />
              Historical trends and projections
            </div>
          </div>
        </div>
      ),
    });

    steps.push({
      id: 'department-overview',
      title: t(locale, 'departmentOverview.title'),
      description: t(locale, 'departmentOverview.description'),
      icon: Layers,
      action: { label: t(locale, 'departmentOverview.action'), path: '/departments' },
      content: (
        <div className="text-center py-4 animate-fadeInUp">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-lg">
            <Layers className="w-7 h-7" />
          </div>
          <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">
            Drill down into individual department budgets and performance.
          </p>
        </div>
      ),
    });

    steps.push({
      id: 'budget-model',
      title: t(locale, 'budgetModel.title'),
      description: t(locale, 'budgetModel.description'),
      icon: Target,
      action: { label: t(locale, 'budgetModel.action'), path: '/budget/model' },
      content: (
        <div className="space-y-3 animate-fadeInUp">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 text-white shadow-lg mx-auto block">
            <Target className="w-7 h-7" />
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
              <Check className="w-4 h-4 text-green-500" />
              Allocation rules and formulas
            </div>
            <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
              <Check className="w-4 h-4 text-green-500" />
              Cost drivers and metrics
            </div>
            <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
              <Check className="w-4 h-4 text-green-500" />
              Model different allocation approaches
            </div>
          </div>
        </div>
      ),
    });

    steps.push({
      id: 'budget-vs-actual',
      title: t(locale, 'budgetVsActual.title'),
      description: t(locale, 'budgetVsActual.description'),
      icon: TrendingUp,
      action: { label: t(locale, 'budgetVsActual.action'), path: '/reports/budget-vs-actual' },
      content: (
        <div className="text-center py-4 animate-fadeInUp">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-teal-500 to-cyan-600 text-white shadow-lg">
            <TrendingUp className="w-7 h-7" />
          </div>
          <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">
            Compare planned budgets against actual spending with variance analysis.
          </p>
        </div>
      ),
    });

    // Capital Planning steps
    steps.push({
      id: 'capital-projects',
      title: t(locale, 'capitalProjects.title'),
      description: t(locale, 'capitalProjects.description'),
      icon: Building,
      action: { label: t(locale, 'capitalProjects.action'), path: '/capital/projects' },
      content: (
        <div className="space-y-3 animate-fadeInUp">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-stone-500 to-stone-700 text-white shadow-lg mx-auto block">
            <Building className="w-7 h-7" />
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
              <Check className="w-4 h-4 text-green-500" />
              Project timelines and milestones
            </div>
            <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
              <Check className="w-4 h-4 text-green-500" />
              Funding sources and draws
            </div>
            <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
              <Check className="w-4 h-4 text-green-500" />
              Current status and progress
            </div>
          </div>
        </div>
      ),
    });

    steps.push({
      id: 'capital-planning',
      title: t(locale, 'capitalPlanning.title'),
      description: t(locale, 'capitalPlanning.description'),
      icon: Briefcase,
      action: { label: t(locale, 'capitalPlanning.action'), path: '/capital/planning' },
      content: (
        <div className="text-center py-4 animate-fadeInUp">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-zinc-500 to-zinc-700 text-white shadow-lg">
            <Briefcase className="w-7 h-7" />
          </div>
          <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">
            Plan multi-year capital investments and prioritize projects.
          </p>
        </div>
      ),
    });

    steps.push({
      id: 'capital-portfolio',
      title: t(locale, 'capitalPortfolio.title'),
      description: t(locale, 'capitalPortfolio.description'),
      icon: FolderOpen,
      action: { label: t(locale, 'capitalPortfolio.action'), path: '/capital/portfolio' },
      content: (
        <div className="text-center py-4 animate-fadeInUp">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-neutral-500 to-neutral-700 text-white shadow-lg">
            <FolderOpen className="w-7 h-7" />
          </div>
          <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">
            Dashboard view of your entire capital project portfolio.
          </p>
        </div>
      ),
    });

    // Tuition & Enrollment steps
    steps.push({
      id: 'fee-projection',
      title: t(locale, 'feeProjection.title'),
      description: t(locale, 'feeProjection.description'),
      icon: GraduationCap,
      action: { label: t(locale, 'feeProjection.action'), path: '/tuition/projections' },
      content: (
        <div className="space-y-3 animate-fadeInUp">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-sky-500 to-blue-600 text-white shadow-lg mx-auto block">
            <GraduationCap className="w-7 h-7" />
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
              <Check className="w-4 h-4 text-green-500" />
              Rate increase modeling
            </div>
            <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
              <Check className="w-4 h-4 text-green-500" />
              Revenue impact analysis
            </div>
            <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
              <Check className="w-4 h-4 text-green-500" />
              Compare fee scenarios
            </div>
          </div>
        </div>
      ),
    });

    steps.push({
      id: 'enrollment-funnel',
      title: t(locale, 'enrollmentFunnel.title'),
      description: t(locale, 'enrollmentFunnel.description'),
      icon: TrendingUp,
      action: { label: t(locale, 'enrollmentFunnel.action'), path: '/enrollment/funnel' },
      content: (
        <div className="text-center py-4 animate-fadeInUp">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 text-white shadow-lg">
            <TrendingUp className="w-7 h-7" />
          </div>
          <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">
            Track admissions pipeline and forecast enrollment numbers.
          </p>
        </div>
      ),
    });

    // Extended Research steps
    steps.push({
      id: 'research-explorer',
      title: t(locale, 'researchExplorer.title'),
      description: t(locale, 'researchExplorer.description'),
      icon: Search,
      action: { label: t(locale, 'researchExplorer.action'), path: '/research/explorer' },
      content: (
        <div className="text-center py-4 animate-fadeInUp">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-cyan-500 to-teal-600 text-white shadow-lg">
            <Search className="w-7 h-7" />
          </div>
          <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">
            Search and analyze your institution&apos;s research portfolio.
          </p>
        </div>
      ),
    });

    steps.push({
      id: 'research-proposals',
      title: t(locale, 'researchProposals.title'),
      description: t(locale, 'researchProposals.description'),
      icon: FileText,
      action: { label: t(locale, 'researchProposals.action'), path: '/research/proposals' },
      content: (
        <div className="text-center py-4 animate-fadeInUp">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-cyan-600 text-white shadow-lg">
            <FileText className="w-7 h-7" />
          </div>
          <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">
            Track proposal submissions, success rates, and pipeline.
          </p>
        </div>
      ),
    });

    steps.push({
      id: 'research-subawards',
      title: t(locale, 'researchSubawards.title'),
      description: t(locale, 'researchSubawards.description'),
      icon: Users,
      action: { label: t(locale, 'researchSubawards.action'), path: '/research/subawards' },
      content: (
        <div className="text-center py-4 animate-fadeInUp">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-teal-500 to-emerald-600 text-white shadow-lg">
            <Users className="w-7 h-7" />
          </div>
          <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">
            Manage subaward agreements and monitor subrecipient spending.
          </p>
        </div>
      ),
    });

    steps.push({
      id: 'research-budget-builder',
      title: t(locale, 'researchBudgetBuilder.title'),
      description: t(locale, 'researchBudgetBuilder.description'),
      icon: Calculator,
      action: { label: t(locale, 'researchBudgetBuilder.action'), path: '/research/budget-builder' },
      content: (
        <div className="space-y-3 animate-fadeInUp">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-emerald-500 to-green-600 text-white shadow-lg mx-auto block">
            <Calculator className="w-7 h-7" />
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
              <Check className="w-4 h-4 text-green-500" />
              Sponsor-specific templates
            </div>
            <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
              <Check className="w-4 h-4 text-green-500" />
              Current F&A and fringe rates
            </div>
            <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
              <Check className="w-4 h-4 text-green-500" />
              Compliance validation
            </div>
          </div>
        </div>
      ),
    });

    steps.push({
      id: 'research-closeout',
      title: t(locale, 'researchCloseout.title'),
      description: t(locale, 'researchCloseout.description'),
      icon: ClipboardCheck,
      action: { label: t(locale, 'researchCloseout.action'), path: '/research/closeout' },
      content: (
        <div className="text-center py-4 animate-fadeInUp">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-amber-500 to-yellow-600 text-white shadow-lg">
            <ClipboardCheck className="w-7 h-7" />
          </div>
          <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">
            Track grants approaching end dates and prepare for closeout.
          </p>
        </div>
      ),
    });

    steps.push({
      id: 'research-compliance',
      title: t(locale, 'researchCompliance.title'),
      description: t(locale, 'researchCompliance.description'),
      icon: AlertTriangle,
      action: { label: t(locale, 'researchCompliance.action'), path: '/research/compliance' },
      content: (
        <div className="text-center py-4 animate-fadeInUp">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-red-500 to-orange-600 text-white shadow-lg">
            <AlertTriangle className="w-7 h-7" />
          </div>
          <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">
            Monitor compliance issues and research risk indicators.
          </p>
        </div>
      ),
    });

    // Analytics & Reporting steps
    steps.push({
      id: 'analytics-bi',
      title: t(locale, 'analyticsBi.title'),
      description: t(locale, 'analyticsBi.description'),
      icon: BarChart3,
      action: { label: t(locale, 'analyticsBi.action'), path: '/analytics' },
      content: (
        <div className="space-y-3 animate-fadeInUp">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 text-white shadow-lg mx-auto block">
            <BarChart3 className="w-7 h-7" />
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
              <Check className="w-4 h-4 text-green-500" />
              Pre-built dashboards
            </div>
            <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
              <Check className="w-4 h-4 text-green-500" />
              Custom report builder
            </div>
            <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
              <Check className="w-4 h-4 text-green-500" />
              Export to multiple formats
            </div>
          </div>
        </div>
      ),
    });

    steps.push({
      id: 'budget-book',
      title: t(locale, 'budgetBook.title'),
      description: t(locale, 'budgetBook.description'),
      icon: BookOpen,
      action: { label: t(locale, 'budgetBook.action'), path: '/reports/budget-book' },
      content: (
        <div className="text-center py-4 animate-fadeInUp">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-indigo-500 to-blue-600 text-white shadow-lg">
            <BookOpen className="w-7 h-7" />
          </div>
          <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">
            Generate formal budget book reports for board presentations.
          </p>
        </div>
      ),
    });

    steps.push({
      id: 'deficit-centers',
      title: t(locale, 'deficitCenters.title'),
      description: t(locale, 'deficitCenters.description'),
      icon: AlertTriangle,
      action: { label: t(locale, 'deficitCenters.action'), path: '/reports/deficit-centers' },
      content: (
        <div className="text-center py-4 animate-fadeInUp">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-rose-500 to-red-600 text-white shadow-lg">
            <AlertTriangle className="w-7 h-7" />
          </div>
          <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">
            Identify and monitor cost centers with deficit balances.
          </p>
        </div>
      ),
    });

    // Workflow & Approvals steps
    steps.push({
      id: 'approval-queue',
      title: t(locale, 'approvalQueue.title'),
      description: t(locale, 'approvalQueue.description'),
      icon: ListChecks,
      action: { label: t(locale, 'approvalQueue.action'), path: '/approvals' },
      content: (
        <div className="space-y-3 animate-fadeInUp">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 text-white shadow-lg mx-auto block">
            <ListChecks className="w-7 h-7" />
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
              <Check className="w-4 h-4 text-green-500" />
              Batch approve/reject
            </div>
            <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
              <Check className="w-4 h-4 text-green-500" />
              Delegate approvals
            </div>
            <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
              <Check className="w-4 h-4 text-green-500" />
              Approval history
            </div>
          </div>
        </div>
      ),
    });

    steps.push({
      id: 'workflow-builder',
      title: t(locale, 'workflowBuilder.title'),
      description: t(locale, 'workflowBuilder.description'),
      icon: Workflow,
      action: { label: t(locale, 'workflowBuilder.action'), path: '/admin/workflows' },
      content: (
        <div className="space-y-3 animate-fadeInUp">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-fuchsia-500 to-pink-600 text-white shadow-lg mx-auto block">
            <Workflow className="w-7 h-7" />
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
              <Check className="w-4 h-4 text-green-500" />
              Visual workflow designer
            </div>
            <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
              <Check className="w-4 h-4 text-green-500" />
              Conditional routing
            </div>
            <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
              <Check className="w-4 h-4 text-green-500" />
              Automated notifications
            </div>
          </div>
        </div>
      ),
    });

    // Help & Settings steps
    steps.push({
      id: 'help-center',
      title: t(locale, 'helpCenter.title'),
      description: t(locale, 'helpCenter.description'),
      icon: HelpCircle,
      action: { label: t(locale, 'helpCenter.action'), path: '/help' },
      content: (
        <div className="text-center py-4 animate-fadeInUp">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-sky-500 to-cyan-600 text-white shadow-lg">
            <HelpCircle className="w-7 h-7" />
          </div>
          <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">
            Access documentation, tutorials, and support resources.
          </p>
        </div>
      ),
    });

    steps.push({
      id: 'account-settings',
      title: t(locale, 'accountSettings.title'),
      description: t(locale, 'accountSettings.description'),
      icon: UserCircle,
      action: { label: t(locale, 'accountSettings.action'), path: '/settings' },
      content: (
        <div className="text-center py-4 animate-fadeInUp">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-slate-500 to-gray-600 text-white shadow-lg">
            <UserCircle className="w-7 h-7" />
          </div>
          <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">
            Customize your profile, preferences, and notifications.
          </p>
        </div>
      ),
    });

    // Forms & Dynamic Submissions steps
    steps.push({
      id: 'form-builder',
      title: t(locale, 'formBuilder.title'),
      description: t(locale, 'formBuilder.description'),
      icon: FormInput,
      action: { label: t(locale, 'formBuilder.action'), path: '/admin/forms' },
      content: (
        <div className="space-y-3 animate-fadeInUp">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-pink-500 to-rose-600 text-white shadow-lg mx-auto block">
            <FormInput className="w-7 h-7" />
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
              <Check className="w-4 h-4 text-green-500" />
              Drag-and-drop field types
            </div>
            <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
              <Check className="w-4 h-4 text-green-500" />
              Conditional logic and validation
            </div>
            <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
              <Check className="w-4 h-4 text-green-500" />
              Submission workflow integration
            </div>
          </div>
        </div>
      ),
    });

    steps.push({
      id: 'form-submissions',
      title: t(locale, 'formSubmissions.title'),
      description: t(locale, 'formSubmissions.description'),
      icon: FileOutput,
      action: { label: t(locale, 'formSubmissions.action'), path: '/forms/submissions' },
      content: (
        <div className="text-center py-4 animate-fadeInUp">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-orange-500 to-amber-600 text-white shadow-lg">
            <FileOutput className="w-7 h-7" />
          </div>
          <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">
            Review, process, and export form submission data.
          </p>
        </div>
      ),
    });

    steps.push({
      id: 'public-forms',
      title: t(locale, 'publicForms.title'),
      description: t(locale, 'publicForms.description'),
      icon: Globe,
      action: { label: t(locale, 'publicForms.action'), path: '/admin/forms' },
      content: (
        <div className="text-center py-4 animate-fadeInUp">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 text-white shadow-lg">
            <Globe className="w-7 h-7" />
          </div>
          <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">
            Share forms externally with customizable public links.
          </p>
        </div>
      ),
    });

    // Research Commitments step
    steps.push({
      id: 'research-commitments',
      title: t(locale, 'researchCommitments.title'),
      description: t(locale, 'researchCommitments.description'),
      icon: Handshake,
      action: { label: t(locale, 'researchCommitments.action'), path: '/research/commitments' },
      content: (
        <div className="text-center py-4 animate-fadeInUp">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-purple-500 to-violet-600 text-white shadow-lg">
            <Handshake className="w-7 h-7" />
          </div>
          <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">
            Track cost-sharing and matching obligations for research grants.
          </p>
        </div>
      ),
    });

    // Finance feature steps
    steps.push({
      id: 'revenue-recognition',
      title: t(locale, 'revenueRecognition.title'),
      description: t(locale, 'revenueRecognition.description'),
      icon: Receipt,
      action: { label: t(locale, 'revenueRecognition.action'), path: '/finance/revenue-recognition' },
      content: (
        <div className="text-center py-4 animate-fadeInUp">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-lg">
            <Receipt className="w-7 h-7" />
          </div>
          <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">
            Manage deferred revenue schedules and ASC 606 compliance.
          </p>
        </div>
      ),
    });

    steps.push({
      id: 'program-margins',
      title: t(locale, 'programMargins.title'),
      description: t(locale, 'programMargins.description'),
      icon: TrendingDown,
      action: { label: t(locale, 'programMargins.action'), path: '/finance/program-margins' },
      content: (
        <div className="text-center py-4 animate-fadeInUp">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-lime-500 to-green-600 text-white shadow-lg">
            <TrendingDown className="w-7 h-7" />
          </div>
          <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">
            Analyze program profitability and cost allocation.
          </p>
        </div>
      ),
    });

    steps.push({
      id: 'personnel-whatif',
      title: t(locale, 'personnelWhatIf.title'),
      description: t(locale, 'personnelWhatIf.description'),
      icon: UserPlus,
      action: { label: t(locale, 'personnelWhatIf.action'), path: '/hr/whatif' },
      content: (
        <div className="text-center py-4 animate-fadeInUp">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-lg">
            <UserPlus className="w-7 h-7" />
          </div>
          <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">
            Model staffing scenarios and see financial impact.
          </p>
        </div>
      ),
    });

    // Add admin-specific step
    if (isAdmin) {
      steps.push({
        id: 'admin',
        title: t(locale, 'admin.title'),
        description: t(locale, 'admin.description'),
        icon: Settings,
        action: { label: t(locale, 'admin.action'), path: '/admin', newTab: true },
        content: (
          <div className="space-y-3 animate-fadeInUp">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              The Admin panel gives you control over:
            </p>
            <ul className="space-y-2 text-sm">
              <li className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                <Check className="w-4 h-4 text-green-500" />
                {t(locale, 'admin.features.users')}
              </li>
              <li className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                <Check className="w-4 h-4 text-green-500" />
                {t(locale, 'admin.features.workflow')}
              </li>
              <li className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                <Check className="w-4 h-4 text-green-500" />
                {t(locale, 'admin.features.settings')}
              </li>
            </ul>
          </div>
        ),
      });
    }

    // Final step
    steps.push({
      id: 'complete',
      title: t(locale, 'complete.title'),
      description: t(locale, 'complete.description'),
      icon: Sparkles,
      content: (
        <div className="text-center py-6 animate-fadeInUp">
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center animate-pulse-glow">
            <Check className="w-10 h-10 text-white" />
          </div>
          <p className="text-slate-600 dark:text-slate-400 max-w-md mx-auto">
            {t(locale, 'complete.content')}
          </p>
        </div>
      ),
    });

    const goalConfig = typeof selectedGoal === 'string' ? USER_GOALS[selectedGoal] : selectedGoal;
    let filteredSteps = steps;

    // Filter by goal if specified
    if (goalConfig?.steps?.length) {
      const allowedSteps = new Set(goalConfig.steps);
      filteredSteps = steps.filter(step => allowedSteps.has(step.id));
      if (filteredSteps.length === 0) filteredSteps = steps;
    }

    // Filter by tour depth
    if (tourDepth === 'lite') {
      // In lite mode, keep only essential steps (welcome, dashboard, search, one feature highlight per category, complete)
      // Also skip steps marked as detailedOnly
      const liteEssentialIds = new Set([
        'welcome',
        'dashboard',
        'search',
        'quick-actions',
        'complete',
        // Keep first step of each category for the selected goal
        'budget-transfers',
        'research-portfolio',
        'positions',
        'capital-projects',
        'fee-projection',
        'analytics-bi',
        'approval-queue',
        'campus-summary',
        'admin',
        'help-center',
      ]);

      filteredSteps = filteredSteps.filter(step => {
        // Always exclude detailed-only steps in lite mode
        if (step.detailedOnly) return false;
        // Keep essential steps
        return liteEssentialIds.has(step.id);
      });

      // Ensure we have at least welcome and complete
      if (!filteredSteps.some(s => s.id === 'welcome')) {
        const welcomeStep = steps.find(s => s.id === 'welcome');
        if (welcomeStep) filteredSteps.unshift(welcomeStep);
      }
      if (!filteredSteps.some(s => s.id === 'complete')) {
        const completeStep = steps.find(s => s.id === 'complete');
        if (completeStep) filteredSteps.push(completeStep);
      }
    } else {
      // In detailed mode, exclude lite-only steps
      filteredSteps = filteredSteps.filter(step => !step.liteOnly);
    }

    return filteredSteps;
  }, [locale, user?.name, searchShortcut, isMac, isAdmin, isFinance, isHR, isBudgetManager, userRoles, analytics, selectedGoal, tourDepth, handleTrySearch]);

  const steps = useMemo(() => getSteps(), [getSteps]);
  const lastStepIndex = Math.max(steps.length - 1, 0);
  const safeStepIndex = Math.min(currentStep, lastStepIndex);

  useEffect(() => {
    if (currentStep !== safeStepIndex) {
      setCurrentStep(safeStepIndex);
      saveProgressToLocal(safeStepIndex);
    }
  }, [currentStep, safeStepIndex, saveProgressToLocal]);

  const currentStepData = steps[safeStepIndex];
  const isFirstStep = safeStepIndex === 0;
  const isLastStep = safeStepIndex === steps.length - 1;

  // Track step views
  useEffect(() => {
    if (currentStepData) {
      analytics.trackStepView(currentStepData.id, safeStepIndex);
    }
  }, [safeStepIndex, currentStepData, analytics]);

  // Trigger animation on step change
  useEffect(() => {
    setAnimationKey(prev => prev + 1);
  }, [safeStepIndex]);

  const handleNext = () => {
    if (isLastStep) {
      handleComplete();
    } else {
      const nextStep = safeStepIndex + 1;
      setCurrentStep(nextStep);
      saveProgressToLocal(nextStep);
    }
  };

  const handlePrev = () => {
    if (!isFirstStep) {
      const prevStep = safeStepIndex - 1;
      setCurrentStep(prevStep);
      saveProgressToLocal(prevStep);
    }
  };

  const handleGoToStep = (index) => {
    setCurrentStep(index);
    saveProgressToLocal(index);
  };

  // Sync sandbox mode with minimized state
  useEffect(() => {
    setTourSandboxMode(isMinimized);
    return () => {
      // Cleanup: disable sandbox mode when component unmounts
      setTourSandboxMode(false);
    };
  }, [isMinimized]);

  const handleComplete = async () => {
    setCompleting(true);
    setSaveError(null);
    // Disable sandbox mode before completing
    setTourSandboxMode(false);
    try {
      // Mark onboarding as complete in user settings
      await api.patch('/users/me/settings', {
        onboarding_completed: true,
        onboarding_completed_at: new Date().toISOString(),
      });
      if (refreshUser) await refreshUser();
      clearLocalProgress();
      analytics.trackComplete(steps.length);
      onComplete?.();
    } catch (err) {
      console.error('Failed to save onboarding status:', err);
      setSaveError(t(locale, 'errors.saveFailed'));
      toast.error('Failed to save onboarding status');
    } finally {
      setCompleting(false);
    }
  };

  const handleSkip = async () => {
    // Disable sandbox mode before skipping
    setTourSandboxMode(false);
    try {
      await api.patch('/users/me/settings', {
        onboarding_skipped: true,
      });
      clearLocalProgress();
      analytics.trackSkip(safeStepIndex, steps.length);
    } catch (err) {
      console.error('Failed to save skip status:', err);
    }
    onSkip?.();
  };

  const handleAction = (action) => {
    analytics.trackStepAction(currentStepData.id, 'navigate', { path: action.path });
    if (action.newTab) {
      window.open(action.path, '_blank', 'noopener,noreferrer');
    } else {
      setIsMinimized(true);
      navigate(action.path);
    }
  };

  // Handle "Try It" action from HowToPanel
  const handleHowToTryIt = (tryItAction) => {
    analytics.trackStepAction(currentStepData.id, 'how_to_try_it', {
      action: tryItAction.action || tryItAction.path
    });

    if (tryItAction.action === 'openSearch') {
      // Trigger global search
      handleTrySearch();
    } else if (tryItAction.path) {
      // Navigate to the path
      setIsMinimized(true);
      navigate(tryItAction.path);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isMinimized) return;

      // Ignore if user is typing in an input
      if (
        document.activeElement?.tagName === 'INPUT' ||
        document.activeElement?.tagName === 'TEXTAREA'
      ) {
        return;
      }

      switch (e.key) {
        case 'ArrowRight':
        case 'Enter':
          if (!isLastStep) {
            e.preventDefault();
            handleNext();
          }
          break;
        case 'ArrowLeft':
          if (!isFirstStep) {
            e.preventDefault();
            handlePrev();
          }
          break;
        case 'Escape':
          e.preventDefault();
          handleSkip();
          break;
        default:
          // Check for number keys to jump to steps
          const num = parseInt(e.key, 10);
          if (num >= 1 && num <= steps.length) {
            e.preventDefault();
            handleGoToStep(num - 1);
          }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFirstStep, isLastStep, steps.length, isMinimized]);

  // Cleanup analytics on unmount
  useEffect(() => {
    return () => {
      analytics.cleanup();
    };
  }, [analytics]);

  const StepIcon = currentStepData.icon;

  if (isMinimized) {
    return (
      <>
        {/* Vignette border effect to indicate tour is still active */}
        <div
          className="fixed inset-0 z-40 pointer-events-none"
          style={{
            boxShadow: 'inset 0 0 150px 60px rgba(59, 130, 246, 0.25), inset 0 0 80px 30px rgba(59, 130, 246, 0.2), inset 0 0 30px 10px rgba(59, 130, 246, 0.15)',
            border: '4px solid rgba(59, 130, 246, 0.5)',
          }}
        />
        {/* Top banner across navbar to indicate tour is active */}
        <div
          className="fixed top-0 left-0 right-0 z-[60] pointer-events-none h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-blue-500"
          style={{
            backgroundSize: '200% 100%',
            animation: 'shimmer 2s linear infinite',
          }}
        />
        <div
          className="fixed top-1 left-0 right-0 z-[60] pointer-events-none flex justify-center"
        >
          <div className="px-3 py-1 bg-blue-600 text-white text-xs font-medium rounded-b-lg shadow-lg flex items-center gap-2">
            <FlaskConical className="w-3 h-3" />
            Sandbox Mode - Changes won&apos;t be saved
          </div>
        </div>
        {/* Floating resume card */}
        <div className="fixed bottom-4 right-4 z-50 w-80">
          <div className="rounded-2xl border-2 border-blue-400/50 bg-white dark:bg-slate-900 shadow-2xl p-4">
            <div className="text-xs text-slate-500 dark:text-slate-400">
              {t(locale, 'common.stepOf', {
                current: safeStepIndex + 1,
                total: steps.length
              })}
            </div>
            <div className="mt-1 text-sm font-semibold text-slate-800 dark:text-slate-100">
              {t(locale, 'common.pauseTitle')}
            </div>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              {t(locale, 'common.pauseDescription')}
            </p>
            <div className="mt-3 flex items-center gap-2">
              <button
                onClick={handleResume}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
              >
                <Play className="w-3 h-3" />
                {t(locale, 'common.resumeTour')}
              </button>
              <button
                onClick={handleSkip}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
              >
                {t(locale, 'common.skip')}
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Inject animation styles */}
      <style>{animationStyles}</style>

      {/* Backdrop - hide when spotlight is shown so user can see highlighted element */}
      {!showSpotlight && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm animate-fadeIn" />
      )}

      {/* Spotlight overlay for interactive steps */}
      {showSpotlight && currentStepData.spotlight && (
        <SpotlightOverlay
          targetSelector={currentStepData.spotlight}
          onClickOutside={() => setShowSpotlight(false)}
          onClickTarget={() => {
            setShowSpotlight(false);
            analytics.trackInteractiveFeature(currentStepData.id, 'spotlight_clicked');
          }}
        >
          <div className="text-center">
            <h4 className="font-semibold text-slate-800 dark:text-slate-100 mb-2">
              {currentStepData.title}
            </h4>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
              {currentStepData.description}
            </p>
            <button
              onClick={() => setShowSpotlight(false)}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
            >
              Got it
            </button>
          </div>
        </SpotlightOverlay>
      )}

      {/* Dialog - hide when spotlight is shown so user can see highlighted element */}
      <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${showSpotlight ? 'pointer-events-none opacity-0' : ''}`}>
        <div
          key={animationKey}
          className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden animate-fadeInUp"
        >
          {/* Header */}
          <div className="relative bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-8 text-white overflow-hidden">
            {/* Shimmer effect */}
            <div className="absolute inset-0 animate-shimmer pointer-events-none" />

            <button
              onClick={handleSkip}
              className="absolute top-4 right-4 p-1 rounded-full hover:bg-white/20 transition-colors"
              aria-label="Skip onboarding"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-4 relative">
              <div className="p-3 rounded-xl bg-white/20 backdrop-blur-sm">
                <StepIcon className="w-8 h-8" />
              </div>
              <div>
                <h2 className="text-xl font-bold">{currentStepData.title}</h2>
                <p className="text-white/80 text-sm mt-1">
                  {t(locale, 'common.stepOf', {
                    current: safeStepIndex + 1,
                    total: steps.length
                  })}
                </p>
              </div>
            </div>
          </div>

          {/* Progress Bar with Time Estimate */}
          <div className="px-6 pt-4">
            <ProgressBar steps={steps} currentStep={safeStepIndex} locale={locale} />
          </div>

          {/* Content */}
          <div className="px-6 py-4">
            <p className="text-slate-600 dark:text-slate-400 mb-4">
              {currentStepData.description}
            </p>

            {/* Video/GIF Media */}
            {showMedia && (
              <StepMedia
                stepId={currentStepData.id}
                locale={locale}
                onPlay={() => analytics.trackStepAction(currentStepData.id, 'video_played')}
                onEnd={() => analytics.trackStepAction(currentStepData.id, 'video_ended')}
              />
            )}

            {currentStepData.content}

            {/* How-To Panel for detailed mode */}
            {tourDepth === 'detailed' && getHowToSteps(currentStepData.id) && (
              <HowToPanel
                stepId={currentStepData.id}
                onTryIt={handleHowToTryIt}
                isExpanded={howToExpanded}
                onToggle={() => setHowToExpanded(!howToExpanded)}
              />
            )}

            {/* Audio Controls & Help Link Row */}
            <div className="flex items-center justify-between mt-4">
              {/* Audio narration toggle */}
              {STEP_MEDIA[currentStepData.id]?.audio && (
                <AudioControls
                  stepId={currentStepData.id}
                  locale={locale}
                  {...audioNarration}
                />
              )}

              {/* Help center link */}
              {HELP_ARTICLES[currentStepData.id] && (
                <HelpCenterLink
                  articleId={currentStepData.id}
                  inline
                  showIcon
                >
                  <BookOpen className="w-3 h-3 mr-1" />
                  Learn more
                </HelpCenterLink>
              )}
            </div>

            {/* Interactive spotlight toggle */}
            {currentStepData.spotlight && (
              <div className="mt-4 text-center">
                <button
                  onClick={() => {
                    // Close any open modals (like GlobalSearch) before showing spotlight
                    document.dispatchEvent(new CustomEvent('closeGlobalSearch'));
                    setShowSpotlight(true);
                  }}
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 rounded-lg transition-colors"
                >
                  <Zap className="w-3 h-3" />
                  Highlight on page
                </button>
              </div>
            )}

            {currentStepData.action && (
              <div className="mt-4 text-center">
                <button
                  onClick={() => handleAction(currentStepData.action)}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                >
                  {currentStepData.action.label}
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

          {/* Progress dots */}
          <div className="flex justify-center gap-2 pb-4">
            {steps.map((step, index) => (
              <button
                key={step.id}
                onClick={() => handleGoToStep(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  index === safeStepIndex
                    ? 'bg-blue-600 w-6'
                    : index < safeStepIndex
                    ? 'bg-blue-300'
                    : 'bg-slate-300 dark:bg-slate-700'
                }`}
                aria-label={`Go to step ${index + 1}: ${step.title}`}
              />
            ))}
          </div>

          {/* Keyboard hint */}
          <div className="flex justify-center pb-2">
            <div className="inline-flex items-center gap-1 text-xs text-slate-400 dark:text-slate-500">
              <Keyboard className="w-3 h-3" />
              <span>Use arrow keys to navigate, Esc to skip</span>
            </div>
          </div>

          {/* Error message */}
          {saveError && (
            <div className="mx-6 mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700 dark:text-red-300">{saveError}</p>
            </div>
          )}

          {/* Footer */}
          <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between">
            <button
              onClick={handlePrev}
              disabled={isFirstStep}
              className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                isFirstStep
                  ? 'text-slate-400 cursor-not-allowed'
                  : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
              }`}
            >
              <ChevronLeft className="w-4 h-4" />
              {t(locale, 'common.back')}
            </button>

            <button
              onClick={handleNext}
              disabled={completing}
              className="inline-flex items-center gap-2 px-6 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-lg transition-colors"
            >
              {completing ? (
                t(locale, 'common.saving')
              ) : isLastStep ? (
                <>
                  {t(locale, 'common.getStarted')}
                  <Sparkles className="w-4 h-4" />
                </>
              ) : (
                <>
                  {t(locale, 'common.next')}
                  <ChevronRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default OnboardingWizard;
